import { useProfile } from '@/api/profile';
import type { UserRole } from '@/types';

const ROLE_HIERARCHY: Record<UserRole, number> = {
  super_admin: 100,
  admin: 80,
  supervisor: 60,
  instructor: 40,
  employee: 20,
};

export function usePermissions() {
  const { data: profile } = useProfile();
  // Primary role (display). The effective SET drives capability checks so a
  // person whose primary is below a gate still passes via a secondary role
  // (N2 multi-rol — e.g. Supervisor + Instructor).
  const role: UserRole = profile?.role ?? 'employee';
  const roles: UserRole[] = profile?.roles ?? (profile?.role ? [profile.role] : ['employee']);

  // Privilege ceiling = the highest level across the effective set.
  const ceiling = roles.reduce((max, r) => Math.max(max, ROLE_HIERARCHY[r] ?? 0), 0);

  const hasMinRole = (minRole: UserRole): boolean => {
    return ceiling >= ROLE_HIERARCHY[minRole];
  };

  // Instructor is a CAPABILITY, not a ladder rung: supervisor does NOT outrank
  // (and thus is not) an instructor — they are orthogonal roles. `isInstructor`
  // is therefore literal membership, not `hasMinRole`.
  const isInstructor = roles.includes('instructor');
  // Who may use the in-app instructor tools. Instructor is a capability, not a
  // ladder rung, so a pure supervisor is excluded; admin/super_admin are included
  // (they have cross-cutting access and the observations endpoint — the one that
  // 403'd — authorizes them). Note: some instructor endpoints still authorize by
  // hierarchy (requireMinRole('instructor')), which a supervisor passes at the
  // API; this gate governs only what the UI OFFERS. Tightening those endpoints to
  // literal-role is a separate decision.
  const canInstruct = isInstructor || hasMinRole('admin');

  return {
    role,
    roles,
    hasMinRole,
    isInstructor,
    canInstruct,
    isSupervisor: hasMinRole('supervisor'),
    isAdmin: hasMinRole('admin'),
  };
}
